import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Edit2, Trash2, CheckIcon, XIcon, MoreVertical, User as UserIcon, ArrowRight, Pencil, Bot, Plus } from 'lucide-react';
import { commentsAPI } from '../services/api';
import { parseUTCTimestamp } from '../utils/dateUtils';
import { useNotification } from '../hooks/useNotification';
import type { TodoComment, TodoCommentCreate, User } from '../types';
import { CommentType } from '../types';

interface TodoCommentsProps {
  todoId: string;
  currentUser?: User;
  isPersonalMode?: boolean;
  createdTime?: string;
}

interface CommentItemProps {
  comment: TodoComment;
  currentUser?: User;
  onEdit: (commentId: string, content: string) => void;
  onDelete: (commentId: string) => void;
  isPersonalMode?: boolean;
}

interface HistoryEntryProps {
  comment: TodoComment;
}

const HistoryEntry: React.FC<HistoryEntryProps> = ({ comment }) => {
  const isAgent = comment.comment_metadata?.source === 'ai_agent';

  const formatDate = (dateString: string) => {
    const date = parseUTCTimestamp(dateString);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) {
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  const IconComponent = comment.comment_type === CommentType.CREATION
    ? Plus
    : comment.comment_type === CommentType.STATUS_CHANGE
      ? ArrowRight
      : comment.comment_type === CommentType.ASSIGNMENT
        ? UserIcon
        : Pencil;

  const lines = comment.content.split('\n');

  return (
    <div className="flex items-start space-x-2 px-3 py-1.5">
      <div className="flex-shrink-0 mt-0.5">
        {isAgent ? (
          <Bot className="w-3.5 h-3.5 text-purple-400" />
        ) : (
          <IconComponent className="w-3.5 h-3.5 text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500">
          {isAgent && <span className="text-purple-500 font-medium">AI: </span>}
          {lines.map((line, i) => (
            <span key={i}>
              {line}
              {i < lines.length - 1 && <br />}
            </span>
          ))}
          <span className="ml-2 text-gray-400">{formatDate(comment.created_time)}</span>
        </div>
      </div>
    </div>
  );
};

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUser,
  onEdit,
  onDelete,
  isPersonalMode = true
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showActions, setShowActions] = useState(false);

  const isAuthor = currentUser?.id === comment.user_id;
  const showUserInfo = !isPersonalMode;

  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== comment.content) {
      onEdit(comment.id, editContent.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  const formatDate = (dateString: string) => {
    const date = parseUTCTimestamp(dateString);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
  };

  return (
    <div className="flex space-x-3 p-3 hover:bg-gray-50 rounded-lg group relative">
      {/* User Avatar */}
      <div className="flex-shrink-0">
        {comment.user.picture ? (
          <img
            src={comment.user.picture}
            alt={comment.user.name}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
            <UserIcon className="w-4 h-4 text-gray-600" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {/* Header with user info and timestamp */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2">
            {showUserInfo && (
              <span className="text-sm font-medium text-gray-900">
                {comment.user.name}
              </span>
            )}
            <span className="text-xs text-gray-500">
              {formatDate(comment.created_time)}
              {comment.is_edited && (
                <span className="ml-1 text-gray-400">(edited)</span>
              )}
            </span>
          </div>

          {/* Actions menu */}
          {isAuthor && (
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition-opacity"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>

              {showActions && (
                <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      onDelete(comment.id);
                      setShowActions(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comment content */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              autoFocus
            />
            <div className="flex space-x-2">
              <button
                onClick={handleSaveEdit}
                disabled={!editContent.trim() || editContent === comment.content}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <CheckIcon className="w-3 h-3 mr-1" />
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 flex items-center"
              >
                <XIcon className="w-3 h-3 mr-1" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-900 whitespace-pre-wrap">
            {comment.content}
          </div>
        )}
      </div>
    </div>
  );
};

const TodoComments: React.FC<TodoCommentsProps> = ({
  todoId,
  currentUser,
  isPersonalMode = true,
  createdTime,
}) => {
  const [comments, setComments] = useState<TodoComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadComments();
  }, [todoId]);

  const loadComments = async () => {
    try {
      setIsLoading(true);
      const fetchedComments = await commentsAPI.getByTodo(todoId);
      setComments(fetchedComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
      showNotification('error', 'Failed to load comments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setIsSubmitting(true);
      const commentData: TodoCommentCreate = {
        content: newComment.trim(),
      };

      const createdComment = await commentsAPI.create(todoId, commentData);
      setComments([...comments, createdComment]);
      setNewComment('');
      showNotification('success', 'Comment added successfully');
    } catch (error) {
      console.error('Failed to create comment:', error);
      showNotification('error', 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string, content: string) => {
    try {
      const updatedComment = await commentsAPI.update(commentId, { content });
      setComments(comments.map(c =>
        c.id === commentId ? updatedComment : c
      ));
      showNotification('success', 'Comment updated successfully');
    } catch (error) {
      console.error('Failed to update comment:', error);
      showNotification('error', 'Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;

    try {
      await commentsAPI.delete(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      showNotification('success', 'Comment deleted successfully');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      showNotification('error', 'Failed to delete comment');
    }
  };

  // Synthesize a "Task created" entry from the todo's created_time
  const creationEntry: TodoComment | null = createdTime ? {
    id: '__created__',
    todo_id: todoId,
    user_id: currentUser?.id ?? '',
    content: 'Task created',
    created_time: createdTime,
    updated_time: createdTime,
    is_edited: false,
    is_deleted: false,
    comment_type: CommentType.CREATION,
    comment_metadata: {},
    user: currentUser ?? { id: '', email: '', name: '', created_time: '', last_login_time: '', is_active: true, preferences: {} },
  } : null;

  const allActivity = creationEntry ? [creationEntry, ...comments] : comments;

  return (
    <div className="border-t border-gray-200 pt-4">
      <div className="flex items-center space-x-2 mb-4">
        <MessageCircle className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-medium text-gray-900">
          Activity ({allActivity.length})
        </h3>
      </div>

      {/* Activity list */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="animate-pulse flex space-x-3 p-3">
              <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-300 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      ) : allActivity.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No activity yet</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {allActivity.map((comment) => (
            comment.comment_type !== CommentType.COMMENT ? (
              <HistoryEntry key={comment.id} comment={comment} />
            ) : (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUser={currentUser}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
                isPersonalMode={isPersonalMode}
              />
            )
          ))}
        </div>
      )}

      {/* Add new comment */}
      <div className="mt-4 space-y-3">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
        <div className="flex justify-end">
          <button
            onClick={handleAddComment}
            disabled={!newComment.trim() || isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <Send className="w-4 h-4 mr-2" />
            {isSubmitting ? 'Adding...' : 'Add Comment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodoComments;
